import React, { Component } from 'react';
import { Container, Row, Col } from 'react-grid-system';

import { InputGroup } from 'components/input';
import { InfoTooltip } from 'components/tooltip';
import { I18n, translate } from 'components/translate';
import merge from 'lodash/merge';
import { inject, observer } from 'mobx-react';
import { ENGAGEMENT_LABELS } from 'modules/userManager/constants';
import DeepSearchInputRange from 'modules/userManager/userSearch/deepSearch/commons/DeepSearchInputRange';
import {
  userFilterFields,
  filters
} from 'modules/userManager/userSearch/deepSearch/constants';
import DeepSearchService from 'modules/userManager/userSearch/deepSearch/DeepSearchService';
import PropTypes from 'prop-types';

import DeepSearchRow from '../commons/DeepSearchRow';
import DeepSearchSelect from '../commons/DeepSearchSelect';

import './DeepSearchUser.scss';

const maxRange = {
  min: 0,
  max: 4
};

const filterCount = 'userFilterCount';

const options = {
  userLocaleList: 'userLocaleList',
  userTypeList: 'userTypeList'
};

const clearFunctionKey = 'deepSearchUser';

const initialState = {
  userName: '',
  userEmail: '',
  userEngagement: maxRange,
  userLocale: {
    value: '',
    open: false
  },
  userJoinType: {
    value: '',
    open: false
  },
  userType: {
    value: '',
    open: false
  }
};

@translate()
@inject((stores) => ({
  userFilter: stores.DeepSearchStore.deepSearch.userFilter
}))
@observer
export default class DeepSearchUser extends Component {
  static propTypes = {
    setClearFunction: PropTypes.func,
    t: PropTypes.func,
    unsetClearFunction: PropTypes.func,
    userFilter: PropTypes.object
  };

  constructor(props) {
    super(props);

    this.state = merge({}, initialState, {
      userName: props.userFilter.userName.query,
      userEmail: props.userFilter.userEmail.query,
      userLocale: {
        ...initialState.userLocale,
        value:
          props.userFilter.userLocale.option ||
          props.userFilter.userLocale.query
      },
      userType: {
        ...initialState.userType,
        value:
          props.userFilter.userType.option || props.userFilter.userType.query
      },
      userJoinType: {
        ...initialState.userJoinType,
        value:
          props.userFilter.userJoinType.option ||
          props.userFilter.userJoinType.query
      },
      userEngagement: {
        ...initialState.userEngagement,
        ...props.userFilter.userEngagement
      }
    });
  }

  componentDidMount() {
    const { setClearFunction } = this.props;

    setClearFunction && setClearFunction(clearFunctionKey, this.clearState);
  }

  componentWillUnmount() {
    const { unsetClearFunction } = this.props;

    unsetClearFunction && unsetClearFunction(clearFunctionKey);
  }

  clearState = () => this.setState({ ...initialState });

  onMenuOpen(field, state) {
    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        open: state
      }
    }));
  }

  setValueSelect(option, field) {
    const { userFilter } = this.props;

    DeepSearchService.updateFilterCount(
      option && option.value,
      userFilter[field].query,
      filterCount
    );

    userFilter[field].option = option || '';
    userFilter[field].query = option ? option.value : '';

    this.setState((prevState) => ({
      [field]: {
        ...prevState[field],
        value: option,
        open: false
      }
    }));
  }

  onChange(event, field) {
    const { userFilter } = this.props;
    const value = event.target.value;

    DeepSearchService.updateFilterCount(
      value,
      userFilter[field].query,
      filterCount
    );

    const fieldValue = { ...userFilter[field], query: value };

    DeepSearchService.setDeepSearchFilter(
      filters.userFilter,
      field,
      fieldValue
    );

    userFilter[field].query = value;

    this.setState({ [field]: value });
  }

  isOnMaxRange = (value) =>
    value.min === maxRange.min && value.max === maxRange.max;

  onChangeInputRange = (field, value) => {
    const { userFilter } = this.props;
    const { min, max } = value;
    const isOnMaxRange = this.isOnMaxRange(value);
    const filter = userFilter[field];
    const shouldIncrementFilterCounter = !isOnMaxRange && !filter.queryData;
    const shouldDecrementFilterCounter = isOnMaxRange && filter.queryData;

    if (shouldIncrementFilterCounter || shouldDecrementFilterCounter) {
      DeepSearchService.updateFilterCount(
        shouldIncrementFilterCounter,
        shouldDecrementFilterCounter,
        filterCount
      );
    }

    DeepSearchService.setDirty(true);

    userFilter[field] = { ...filter, ...value };
    userFilter[field].queryData = !isOnMaxRange;

    this.setState((state) => ({
      [field]: {
        ...state[field],
        min,
        max
      }
    }));
  };

  getLabelClassName = (value) => {
    const styleBase = 'deep__search__user__input-range__label';
    const isTouchingMin = value === maxRange.min;
    const isTouchingMax = value === maxRange.max;
    let modifier = '';

    if (!isTouchingMin && !isTouchingMax) {
      modifier = 'center';
    } else {
      modifier = isTouchingMin ? 'left' : 'right';
    }

    return `${styleBase}--${modifier}`;
  };

  renderInputRangeShadow() {
    const { t } = this.props;

    return (
      <div className="deep__search__user__input-range--shadow">
        {ENGAGEMENT_LABELS.map((label, idx) => (
          <div
            key={`input-rage__label-${idx}`}
            className="deep__search__user__input-range__element"
          >
            <div className="deep__search__user__input-range__label">
              <span className={this.getLabelClassName(idx)}>{t(label)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { userEngagement } = this.state;

    return (
      <Container className="deep__search__inner-container" fluid>
        <Row nogutter>
          <Col xs={12} md={3} className="deep__search__tab-title">
            <I18n label="users_manager.deep_search.lateral_menu.user" />
            <InfoTooltip
              rerenderPosition={true}
              text="users_manager.deep_search.user.tooltip"
            />
          </Col>
          <Col xs={12} md={9} />
        </Row>

        <DeepSearchRow label="general.engagement">
          {this.renderInputRangeShadow()}
          <DeepSearchInputRange
            hideLabel
            maxRange={maxRange}
            field={userFilterFields.userEngagement}
            min={userEngagement.min}
            max={userEngagement.max}
            setInputRange={this.onChangeInputRange}
          />
        </DeepSearchRow>

        <DeepSearchRow label="general.name">
          <InputGroup
            name="userName"
            value={this.state.userName}
            maxLength="100"
            autoComplete="off"
            placeholder="users_manager.deep_search.user.user_name_placeholder"
            onChange={(event) =>
              this.onChange(event, userFilterFields.userName)
            }
          />
        </DeepSearchRow>

        <DeepSearchRow label="general.email">
          <InputGroup
            name="userEmail"
            value={this.state.userEmail}
            maxLength="10000"
            autoComplete="off"
            placeholder="users_manager.deep_search.user.user_email_placeholder"
            onChange={(event) =>
              this.onChange(event, userFilterFields.userEmail)
            }
          />
        </DeepSearchRow>

        <DeepSearchRow label="general.idiom">
          <DeepSearchSelect
            name="userLocale"
            field={userFilterFields.userLocale}
            placeholder="users_manager.deep_search.user.user_locale_placeholder"
            options={options.userLocaleList}
            setValueSelect={this.setValueSelect.bind(this)}
            value={this.state.userLocale.value}
          />
        </DeepSearchRow>

        <DeepSearchRow label="users_manager.deep_search.user.previous_category">
          <DeepSearchSelect
            name="userJoinType"
            field={userFilterFields.userJoinType}
            placeholder="users_manager.deep_search.access.category_placeholder"
            options={options.userTypeList}
            setValueSelect={this.setValueSelect.bind(this)}
            value={this.state.userJoinType.value}
          />
        </DeepSearchRow>

        <DeepSearchRow label="users_manager.deep_search.user.category">
          <DeepSearchSelect
            name="userType"
            field={userFilterFields.userType}
            placeholder="users_manager.deep_search.access.category_placeholder"
            options={options.userTypeList}
            setValueSelect={this.setValueSelect.bind(this)}
            value={this.state.userType.value}
          />
        </DeepSearchRow>
      </Container>
    );
  }
}
